import { useState } from 'react';
import { useCookies } from 'react-cookie';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faEnvelope, faLocationDot, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

import HeaderHome from '../../Components/Header_Home/Header_Home';
import { Reveal, RevealText } from '../../Components/Motion/Motion';
import ZenLogoWhite from "../../imgs/ZenLogo_white.svg";
import '../Home/Home.css';
import './Contact.css';

const EMAIL = "official@zenvolt.ro";

const Contact = ({ data } : { data : any }) => {

    const [cookies] = useCookies();
    const en = cookies?.language === "en";

    const [form, setForm] = useState({ name: "", organisation: "", message: "" });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm(curr => ({ ...curr, [e.target.name]: e.target.value }));
    };

    // No mail server sits behind this page, so the form composes the message and
    // hands it to the visitor's own mail client.
    const sendMail = (e: React.FormEvent) => {
        e.preventDefault();

        const subject = en
            ? `ZenVolt — message from ${form.name || "the site"}`
            : `ZenVolt — mesaj de la ${form.name || "site"}`;

        const body = [
            en ? `Name: ${form.name}` : `Nume: ${form.name}`,
            form.organisation ? (en ? `Organisation: ${form.organisation}` : `Organizație: ${form.organisation}`) : "",
            "",
            form.message,
        ].filter(Boolean).join("\n");

        window.location.href = `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    // Email is the one channel; the rest is what a visitor needs to know before
    // writing — where we are and when an answer arrives.
    const details = [
        {
            icon: faEnvelope,
            label: "Email",
            value: EMAIL,
            href: `mailto:${EMAIL}`,
            note: en ? "The fastest way to reach us." : "Cel mai rapid mod de a ne găsi.",
        },
        {
            icon: faLocationDot,
            label: en ? "Where we are" : "Unde suntem",
            value: en ? "Bucharest, Romania" : "București, România",
            href: null,
            note: en ? "Demos on site, by appointment." : "Demonstrații la fața locului, pe bază de programare.",
        },
        {
            icon: faClock,
            label: en ? "Answer time" : "Timp de răspuns",
            value: en ? "1 – 2 working days" : "1 – 2 zile lucrătoare",
            href: null,
            note: en ? "Written by someone who knows the modules." : "Răspunde cineva care cunoaște modulele.",
        },
    ];

    return (
        <div className="home-container home-page contact-page">
            <HeaderHome data={data} activePage="contact" />

            <div className="content">

                <section className="hp-section ct-top">
                    <header className="hp-section-head">
                        <Reveal as="p" className="hp-kicker">
                            <span className="hp-kicker-dot" />
                            Contact
                        </Reveal>
                        <RevealText
                            className="hp-h2"
                            lines={en
                                ? [[{ text: "Tell us about" }, { text: "your station", accent: true }]]
                                : [[{ text: "Spune-ne despre" }, { text: "stația ta", accent: true }]]}
                        />
                        <Reveal as="p" className="hp-lead ct-lead" delay={140}>
                            {en
                                ? "A demo for a school, accounts for a company's electricians, or a question about a module — write to us and we will come back with an answer, not a brochure."
                                : "O demonstrație pentru o școală, conturi pentru electricienii unei companii sau o întrebare despre un modul — scrie-ne și revenim cu un răspuns, nu cu o broșură."}
                        </Reveal>
                    </header>

                    {/* One panel: the form, and beside it everything worth knowing
                        before writing. Two cards would have left the row half
                        empty now that there is a single channel. */}
                    <Reveal className="ct-panel" from="zoom">
                        <form className="ct-form" onSubmit={sendMail}>
                            <h3>{en ? "Write us a message" : "Scrie-ne un mesaj"}</h3>

                            <div className="ct-row">
                                <label className="ct-field">
                                    <span>{en ? "Name" : "Nume"}</span>
                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        placeholder={en ? "Your name" : "Numele tău"}
                                        required
                                    />
                                </label>

                                <label className="ct-field">
                                    <span>{en ? "School / company" : "Școală / companie"}</span>
                                    <input
                                        type="text"
                                        name="organisation"
                                        value={form.organisation}
                                        onChange={handleChange}
                                        placeholder={en ? "Optional" : "Opțional"}
                                    />
                                </label>
                            </div>

                            <label className="ct-field">
                                <span>{en ? "Message" : "Mesaj"}</span>
                                <textarea
                                    name="message"
                                    value={form.message}
                                    onChange={handleChange}
                                    rows={6}
                                    placeholder={en ? "What would you like to do with ZenVolt?" : "Ce ai vrea să faci cu ZenVolt?"}
                                    required
                                />
                            </label>

                            <div className="ct-actions">
                                <button type="submit" className="hp-cta ct-submit">
                                    {en ? "Send the message" : "Trimite mesajul"} <FontAwesomeIcon icon={faPaperPlane} />
                                </button>
                                <p className="ct-form-note">
                                    {en
                                        ? `Opens your own mail app with the message already written, addressed to ${EMAIL}.`
                                        : `Deschide aplicația ta de email cu mesajul deja scris, către ${EMAIL}.`}
                                </p>
                            </div>
                        </form>

                        <aside className="ct-aside">
                            {details.map((d) => (
                                <div key={d.label} className="ct-item">
                                    <span className="ct-item-icon"><FontAwesomeIcon icon={d.icon} /></span>
                                    <div className="ct-item-text">
                                        <p className="ct-item-label">{d.label}</p>
                                        {d.href
                                            ? <a className="ct-item-value" href={d.href}>{d.value}</a>
                                            : <p className="ct-item-value">{d.value}</p>}
                                        <p className="ct-item-note">{d.note}</p>
                                    </div>
                                </div>
                            ))}
                        </aside>
                    </Reveal>
                </section>

                <section className="hp-final">
                    <Reveal from="zoom" className="hp-final-logo-wrap">
                        <img src={ZenLogoWhite} alt="ZenVolt" className="hp-final-logo" width={90} height="auto" />
                    </Reveal>

                    <RevealText
                        className="hp-final-title"
                        start={.88}
                        end={.5}
                        lines={en
                            ? [[{ text: "Answers come from people", accent: true }], [{ text: "who know the maneuvers." }]]
                            : [[{ text: "Răspund oameni", accent: true }], [{ text: "care știu manevrele." }]]}
                    />

                    <Reveal delay={200}>
                        <a className="hp-cta light" href={`mailto:${EMAIL}`}>
                            {EMAIL} <FontAwesomeIcon icon={faEnvelope} />
                        </a>
                    </Reveal>

                    <p className="hp-final-meta">© {new Date().getFullYear()}. All rights reserved.</p>
                    <p className="hp-final-meta">{en ? "Created by Barbu Mihai." : "Creat de Barbu Mihai."}</p>
                </section>

            </div>
        </div>
    );
};

export default Contact;
